/* eslint-disable jsx-a11y/no-autofocus */
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  PageLayout,
  SubPageBlueprint,
  coreExtensionData,
  createFrontendModule,
  pluginHeaderActionsApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import searchPlugin from '@backstage/plugin-search/alpha';
import {
  DefaultResultListItem,
  SearchContextProvider,
} from '@backstage/plugin-search-react';
import type { SearchResult } from '@backstage/plugin-search-common';
import {
  SearchFilterBlueprint,
  SearchFilterResultTypeBlueprint,
  SearchResultListItemBlueprint,
} from '@backstage/plugin-search-react/alpha';
import { RiSearchLine } from '@remixicon/react';
import type { ResultItemComponent } from '@internal/backstage-plugin-landing-pages';
import {
  CatalogSearchFilters,
  TechDocsSearchFilters,
  TechRadarSearchFilters,
  SearchPageContextProvider,
  SEARCH_RESULT_TYPE,
  SEARCH_TABS,
  ALL_TAB_PATH,
} from '@internal/backstage-plugin-landing-pages';
import {
  softwareCatalogSearchFilterType,
  techdocsSearchFilterType,
} from '@internal/backstage-plugin-hm-common';

const searchPageOverride = searchPlugin.getExtension('page:search').override({
  factory(_originalFactory, { inputs, config }) {
    const title = config.title ?? 'Search';
    const path = config.path ?? '/search';
    const icon = <RiSearchLine />;

    const getResultItemComponent = (
      result: SearchResult,
    ): ResultItemComponent => {
      const value = inputs.items.find(
        item =>
          item
            ?.get(SearchResultListItemBlueprint.dataRefs.item)
            .predicate?.(result),
      );
      return (
        value?.get(SearchResultListItemBlueprint.dataRefs.item).component ??
        DefaultResultListItem
      );
    };

    const resultTypes = inputs.resultTypes.map(item =>
      item.get(SearchFilterResultTypeBlueprint.dataRefs.resultType),
    );

    const searchFilters = inputs.searchFilters.map(
      item => item.get(SearchFilterBlueprint.dataRefs.searchFilters).component,
    );

    const filtersByType: Record<string, (() => JSX.Element)[]> = {
      [ALL_TAB_PATH]: [CatalogSearchFilters],
      [SEARCH_RESULT_TYPE.SOFTWARE_CATALOG]: [CatalogSearchFilters],
      [SEARCH_RESULT_TYPE.TECHDOCS]: [TechDocsSearchFilters],
      [SEARCH_RESULT_TYPE.TECH_RADAR]: [TechRadarSearchFilters],
    };

    const tabs = inputs.pages.map(page => ({
      id: page.get(coreExtensionData.routePath),
      label:
        page.get(coreExtensionData.title) ??
        page.get(coreExtensionData.routePath),
      icon: page.get(coreExtensionData.icon),
      href: page.get(coreExtensionData.routePath),
    }));

    const firstPath = inputs.pages[0]?.get(coreExtensionData.routePath);

    const PageContent = () => {
      const headerActionsApi = useApi(pluginHeaderActionsApiRef);
      const headerActions = headerActionsApi.getPluginHeaderActions('search');

      return (
        <SearchPageContextProvider
          value={{
            config,
            getResultItemComponent,
            resultTypes,
            searchFilters,
            filtersByType,
          }}
        >
          <SearchContextProvider>
            <PageLayout
              title={title}
              icon={icon}
              tabs={tabs}
              headerActions={[...headerActions]}
            >
              <Routes>
                {firstPath && (
                  <Route index element={<Navigate to={firstPath} replace />} />
                )}
                {inputs.pages.map(page => (
                  <Route
                    key={page.get(coreExtensionData.routePath)}
                    path={`${page.get(coreExtensionData.routePath)}/*`}
                    element={page.get(coreExtensionData.reactElement)}
                  />
                ))}
              </Routes>
            </PageLayout>
          </SearchContextProvider>
        </SearchPageContextProvider>
      );
    };

    return [
      coreExtensionData.routePath(path),
      coreExtensionData.routeRef(searchPlugin.routes.root),
      coreExtensionData.reactElement(<PageContent />),
      coreExtensionData.title(title),
      coreExtensionData.icon(icon),
    ];
  },
});

const subPages = SEARCH_TABS.map(tab =>
  SubPageBlueprint.make({
    name: tab.path,
    params: {
      path: tab.path,
      title: tab.title,
      loader: () =>
        import('@internal/backstage-plugin-landing-pages').then(m => (
          <m.SearchTabContent
            path={tab.path}
            title={tab.title}
            icon={tab.icon}
            defaultFilters={tab.defaultFilters}
          />
        )),
    },
  }),
);

export const searchPluginOverride = createFrontendModule({
  pluginId: 'search',
  extensions: [
    searchPageOverride,
    softwareCatalogSearchFilterType,
    techdocsSearchFilterType,
    ...subPages,
  ],
});
